import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useAuthContext } from '../../../../context/auth.context'
import { useRoleSysApiKey } from '../../../../role/hook/role-sys'
import { IconAdd } from '../../../../shared/icon/icon'
import { MenuItem } from '../../../../shared/menu/menu'
import { canSwitchOrganization } from '../../../../shared/organization-switch'
import { getAccess } from '../../org/api-keys/api'
import { noteApiAccess } from '../../org/api-keys/use-api-access'
import { CUSTOM_CHOICE } from '../../org/api-keys/grant-words'
import { isKeyStaff } from '../../org/api-keys/grants-access'
import { ApiKeyCreateDrawer } from '../../org/api-keys/page/api-key-create-drawer'
import { useKeyActions } from '../../org/api-keys/page/api-key-actions'
import { setOrgAccess } from './sys-api'

// Platform -> API keys' add action (owner 2026-10-23), pinned in the Keys
// card's menu (sys-keys-tab.js, owner 2026-10-29): a platform key,
// EducationXR's own. ("Platform", not "system": a key's Phase E "System key"
// is its id space, another thing.) It is made in the platform organization
// (EducationXR's own, platform-organization.js) through the organization's own
// create flow (ApiKeyCreateDrawer; POST /api-key makes a key in the
// organization the session is in), opening on Custom permissions, where the
// platform-only groups (AI tools telemetry) are offered. Staff never pick an
// organization. It is EducationXR staff's in their own session (isKeyStaff):
// absent for anyone else, who could not use it.
//
// If that organization's partner API access is off, the form says so, with
// the reason it will be switched on with ("To create platform key: <name>"),
// and Create switches it on first (PUT /api-key-sys/organization/:id/access,
// as the organization's page does), then makes the key; if the key is then
// refused, the form says the access stayed on. Never silently.
//
// A key is only ever made in the session's organization, so a session that is
// somewhere else is told where to go (Switch organization) rather than shown a
// form that would make an ordinary organization's key.

export const platformKeyReason = name => `To create platform key: ${String(name || '').trim()}`

const PLATFORM_INTRO = organizationName =>
  `A platform key belongs to EducationXR's own organization${organizationName ? ` (${organizationName})` : ''}. ` +
  "Only a platform key can be given platform permissions, such as AI tools telemetry. Its secret is shown once, when it's made."

const ElsewhereDialog = ({ platform, onClose }) => {
  const { state } = useAuthContext()
  const canSwitch = canSwitchOrganization(state && state.jwtClaims)
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  const where = platform.organizationName ? `You're working in ${platform.organizationName}.` : ''
  return createPortal(
    <div className="sw-drawer sw-scrim justify-content-center align-items-center" data-testid="platform-key-elsewhere-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="sw-dialog" role="dialog" aria-modal="true" aria-labelledby="pk-elsewhere-title" data-testid="platform-key-elsewhere">
        <h2 className="h5" id="pk-elsewhere-title">Platform keys are made in EducationXR's own organization</h2>
        <p className="sw-hint">
          {platform.status === 'unknown'
            ? "Whether you're working in EducationXR's own organization couldn't be checked, so no platform key can be made from here right now. Try again in a moment."
            : `${where} A key is made in the organization you're working in, and a platform key belongs to EducationXR's own. ${canSwitch ? 'Switch to it, then choose New platform key again.' : "Your account isn't in another organization to switch to: ask an administrator of EducationXR's organization to make it."}`}
        </p>
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" autoFocus onClick={onClose}>Close</button>
          {canSwitch && platform.status === 'elsewhere' && <Link className="btn sw-btn-primary" to="/auth/account">Switch organization</Link>}
        </div>
      </div>
    </div>,
    document.body
  )
}

// platform: usePlatformOrganization()'s answer. onCreated: after a key is
// made (the page reads its list again). The answer's item is the Keys card's
// pinned New platform key (shown only to staff in their own session); its
// dialogs are the page's to render, outside the card.
export const useNewPlatformKey = ({ platform, onCreated }) => {
  const { state } = useAuthContext()
  const role = useRoleSysApiKey()
  const canSwitchAccess = !!role.accessUpdate
  const [step, setStep] = useState(null) // null | 'checking' | 'create' | 'elsewhere'
  const [access, setAccess] = useState(null)
  const actions = useKeyActions({ onChanged: onCreated })

  const start = async () => {
    if (platform.status !== 'here') return setStep('elsewhere')
    setStep('checking')
    let read = null
    try {
      read = await getAccess()
    } catch (err) {
      // the create says what is wrong, in the server's words
    }
    setAccess(read || null)
    setStep('create')
  }

  const accessOff = !!access && access.enabled === false
  const where = platform.organizationName || "EducationXR's organization"

  const notice = name => {
    if (!accessOff) return null
    if (!canSwitchAccess) {
      return (
        <div className="sw-notice bad mb-2" role="status" data-testid="platform-key-access-notice">
          <div>{`Partner API access is off for ${where}, and switching it on needs the API key update permission, so no platform key can be made.`}</div>
        </div>
      )
    }
    const reason = name.trim() ? platformKeyReason(name) : 'To create platform key: <the key’s name>'
    return (
      <div className="sw-notice warn mb-2" role="status" data-testid="platform-key-access-notice">
        <div>
          {`Partner API access is off for ${where}. Create key switches it on first, then makes the key. The audit log records the switch with the reason “${reason}”.`}
        </div>
      </div>
    )
  }

  // Switches the access on; what the form adds to a refusal of the key that follows.
  const switchOn = async ({ name }) => {
    const answer = await setOrgAccess(platform.organizationID, true, platformKeyReason(name))
    const now = answer && typeof answer === 'object' && 'enabled' in answer ? answer : { ...access, enabled: true }
    setAccess(now)
    noteApiAccess({ enabled: now.enabled, default_rate_limit_per_minute: now.default_rate_limit_per_minute, live_keys: now.live_keys, max_keys: now.max_keys })
    return `Partner API access for ${where} was switched on, and stays on.`
  }

  const created = answer => {
    setStep(null)
    actions.reveal({ name: answer && answer.key ? answer.key.name : '', secret: answer && answer.secret })
    if (onCreated) onCreated()
  }

  const item = (
    <MenuItem key="new-platform-key" Icon={IconAdd} text="New platform key" pin keepOnMobile show={isKeyStaff(state)}
      disabled={!platform || platform.status === 'loading' || step === 'checking'} onClick={start} />
  )

  const dialogs = (
    <>
      {step === 'elsewhere' && <ElsewhereDialog platform={platform} onClose={() => setStep(null)} />}
      {step === 'create' &&
        <ApiKeyCreateDrawer
          open
          title="New platform key"
          intro={PLATFORM_INTRO(platform.organizationName)}
          defaultChoice={CUSTOM_CHOICE}
          notice={notice}
          disabled={accessOff && !canSwitchAccess}
          beforeCreate={accessOff && canSwitchAccess ? switchOn : undefined}
          beforeCreateError="Partner API access couldn't be switched on, so the key wasn't created."
          onClose={() => setStep(null)}
          onCreated={created} />}
      {actions.dialogs}
    </>
  )

  return { item, dialogs }
}
